import { ResponseError } from '@lib/error'
import { DiscoveryChannelCounts, SimilarHandles } from './types'

// --- Contract isolation module (BRAND-9307) ---
// Final as-built shape from Platform's completed BRAND-9307 half. Every
// literal BE-shape assumption for the first-discovery-run flow lives HERE
// and only here — sagas/components consume this module, never raw rel
// strings or endpoint paths.

// HATEOAS rel names on the BRIEF's own top-level `links` array (NOT nested
// inside the `sourcing` block — the sourcing block carries data only).
// Resolved via the existing `selectCampaignLink(state, campaignId, rel)`
// selector, same pattern already used for `interested_candidates` /
// `shortlisted_candidates` (see src/sagas/agentSourcedCreators/fetchCreators.ts).
export const ATTACH_TRANSCRIPT_REL = 'attach_transcript'
export const ATTACH_NOTES_REL = 'attach_notes'
export const START_DISCOVERY_RUN_REL = 'start_discovery_run'

// BRAND-9582 item 13: detach rels — PUT, no body, full brief payload on
// success (same shape as attach). Present only while attached and no
// non-failed run exists (the complement of the attach guard); idempotent.
export const DETACH_TRANSCRIPT_REL = 'detach_transcript'
export const DETACH_NOTES_REL = 'detach_notes'

// start_discovery_run's body is `{ channels: { <channel>: <count> } }`,
// only enabled channels included. BRAND-9664: behind generateRerunCriteriaEnabled,
// a pending free-text note rides on this same body as `note` instead of a
// separate attach_notes PUT — omitted (not even blank) when absent, so a
// toggle-off/no-notes call stays byte-identical to today's.
// BRAND-9688: `similar_handles` rides the same body, omitted entirely when
// absent for the same reason — see IStartDiscoveryRunPayload's doc.
export const buildStartDiscoveryRunBody = (
  channels: DiscoveryChannelCounts,
  notes?: string,
  similarHandles?: SimilarHandles,
) => {
  const body: {
    channels: DiscoveryChannelCounts
    note?: string
    similar_handles?: SimilarHandles
  } = { channels }
  if (notes) body.note = notes
  if (similarHandles) body.similar_handles = similarHandles
  return body
}

// BRAND-9663 (BRAND-9605 tier 2), pending Platform's BRAND-9660/9661
// contract: multipart variant of the same start_discovery_run request —
// carries a fresh transcript file (optional — a rerun's existing one, if
// any, is left untouched server-side when omitted) and an optional note
// (file or text) atomically with channels in one request, so there's no
// window where a separate attach could be followed by the user
// reconsidering back to "reuse". `channels` travels as a JSON string field
// since the rest of the body is multipart. `note` carries either a picked
// file OR typed/edited text under the SAME field name (never both —
// Platform duck-types it); a picked file wins over text when both are
// present.
// transcript is FILE-ONLY here — Platform's start_discovery_run rejects a
// plain string as an invalid file type when generate_criteria is true, so
// an untouched transcript must be omitted entirely rather than resent as
// text.
// `generate_criteria` is conditional, not always sent as 'true' — this
// same bundle is also used on a genuine run 1 (no run history, nothing to
// "regenerate") once the toggle is on, purely to avoid a separate
// attach_transcript/attach_notes call before Start; that case must NOT
// claim generate_criteria. `note` distinguishes undefined (omit the field
// entirely — Platform leaves whatever's already attached untouched) from
// an explicit empty string (send it — Platform treats an absent field as
// "no change," so genuinely clearing a note requires sending '' rather
// than just leaving the field out).
export const buildStartDiscoveryRunFormData = (
  channels: DiscoveryChannelCounts,
  transcriptFile?: File,
  note?: string,
  noteFile?: File,
  generateCriteria?: boolean,
  similarHandles?: SimilarHandles,
): FormData => {
  const formData = new FormData()
  formData.append('channels', JSON.stringify(channels))
  // Sent whenever the caller passed an explicit value at all — including
  // `false` — so an unchecked rerun actually tells Platform not to
  // regenerate, rather than omitting the field and falling into
  // effective_generate_criteria's absent-means-true default. Platform casts
  // via ActiveModel::Type::Boolean, which recognizes the literal string
  // "false".
  if (generateCriteria !== undefined) formData.append('generate_criteria', String(generateCriteria))
  if (transcriptFile) formData.append('transcript', transcriptFile)
  if (noteFile) {
    formData.append('note', noteFile)
  } else if (note !== undefined) {
    formData.append('note', note)
  }
  // BRAND-9688: same "omit entirely when absent" rule as the JSON variant
  // above — a multipart start with no similar handle stays byte-identical
  // to today's request.
  if (similarHandles) formData.append('similar_handles', JSON.stringify(similarHandles))
  return formData
}

// PUT …/sourcing_transcript takes the file under a `file` multipart field,
// matching the existing house pattern (mediaUpload.ts).
export const buildTranscriptFormData = (file: File): FormData => {
  const formData = new FormData()
  formData.append('file', file)
  return formData
}

// PUT …/sourcing_notes takes multipart `file` OR params `note` (singular)
// + optional `note_filename` — per the BRAND-9307 as-built platform
// contract. Both may be present at once (the form lets a brand attach a
// notes file AND type free text in the same "added once" step).
export const buildNotesFormData = (file?: File, notes?: string): FormData => {
  const formData = new FormData()
  if (file) formData.append('file', file)
  if (notes) formData.append('note', notes)
  return formData
}

// Generic multipart POST/PUT — mirrors the fetch+FormData pattern in
// src/utils/mediaUpload.ts, but takes a pre-built FormData so callers can
// attach more than one field (e.g. an optional file PLUS a notes string on
// the same request, which mediaUpload's single-field helper can't do).
export const multipartRequest = (
  method: string,
  url: string,
  formData: FormData,
  token?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`

  return fetch(url, { method, headers, body: formData })
    .then(async (response: Response) => {
      if (!response.ok) throw response
      return response
        .json()
        .then((json) => json)
        .catch(() => response.statusText)
    })
    .catch(async (response: any) => {
      const json = await response.json()
      throw new ResponseError(json, response.status)
    })
}

// House 422-verbatim toast pattern: surface the BE's own message text
// without paraphrasing it. `IResponseError.messages` is either a string or
// an array of `{ error_code, message }` — flatten either shape into one
// human-readable line; fall back to `.message`/a generic string only when
// neither is present (e.g. a network-level failure with no response body).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractVerbatimErrorMessage = (error: any): string => {
  const messages = error?.messages
  if (typeof messages === 'string' && messages.length > 0) return messages
  if (Array.isArray(messages) && messages.length > 0) {
    return (
      messages
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((m: any) => (typeof m === 'string' ? m : m?.message ?? String(m)))
        .join(', ')
    )
  }
  if (typeof error?.message === 'string' && error.message.length > 0 && error.message !== 'Error') {
    return error.message
  }
  return 'Something went wrong. Please try again.'
}

// A `*_already_attached` 422 (e.g. brief.sourcing_transcript.
// transcript_already_attached) means the BE state is AHEAD of the FE — the
// attachment exists server-side but this client hasn't refreshed. Callers
// treat it as effective success: refetch the campaign so
// transcript_attached/note_attached sync, keep the verbatim toast.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAlreadyAttachedError = (error: any): boolean => {
  const messages = error?.messages
  if (!Array.isArray(messages)) return false
  return messages.some(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (m: any) => typeof m?.error_code === 'string' && m.error_code.endsWith('_already_attached'),
  )
}

// note_locked_by_run (BRAND-9604) means notes are permanently frozen by a
// completed/stale run - not a transient failure, and not a "BE is ahead"
// signal either. Same "nothing to send" shape as an absent attach_notes
// rel, just discovered via a 422 instead of a missing link (stale cached
// snapshot). Exact match, not a suffix family like *_already_attached.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNotesLockedError = (error: any): boolean => {
  const messages = error?.messages
  if (!Array.isArray(messages)) return false
  return messages.some(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (m: any) => m?.error_code === 'brief.sourcing_notes.note_locked_by_run',
  )
}
